import React, { createContext, useContext, useState, useCallback } from 'react';
import type { StripeReturnParams } from '../utils/checkout/stripe-return';
import { StripeReturnSuccessResponseSchema } from '../schemas/api/checkout/stripe-return';
import type { z } from 'zod';
import { useUser, useUI, useNotifications, useAppSession, useProductOptional } from './index';
import { useRouterContext } from './RouterProvider';
import { type Stripe } from '@stripe/stripe-js';
import { WhichKey } from '../data/js/stripe';
import { loadStripeSafely } from '../utils/stripe-loader';
import { emit, googleEvent } from '../utils/dom';
import { fetchSearchListItem } from '../data/api/search';
import { getCheckoutSessionId, clearCheckoutSessionId } from '../utils/tracking-context';
import { getPartner } from '../utils/partner';
import { trackFunnelEvent, trackError } from '../utils/track-funnel';
import { toAddressBookRequest } from '../utils/checkout/express-checkout';
import { trackError as logErrorEvent, ErrorSeverity } from '../utils/error-tracking';
import type { ErrorContext } from '../utils/error-tracking';
import { severityForOwnFetchFailure, transportFailureContext } from '../utils/fetch-transport-failure';
import {
  setupAbandonmentTracking,
  updateAbandonmentStep,
  updateAbandonmentMetadata,
  disableAbandonmentTracking,
  endAbandonmentTracking,
} from '../utils/track-abandonment';
import shared from '../data/js/shared';
import {
  computeCheckoutTotals,
  selectShippingRateAtIndex,
  buildAuthenticatedPaymentIntentBody,
  buildGuestPaymentIntentBody,
  buildFunnelUserOptions,
  buildShippingCalculatedEventData,
  buildShippingQuoteResult,
  buildSelectedRateFields,
  type ShippingQuoteOutcome,
  buildCheckoutStartedEventData,
  buildCheckoutStartedAbandonmentContext,
  buildShippingSelectedAbandonmentContext,
  buildShippingSelectedEventData,
  buildPaymentAttemptedEventData,
  buildPaymentFailedEventData,
  buildCheckoutCompleteEventData,
  toTrackedError,
  errorMessageOr,
  type PaymentMethodSource,
} from '../utils/checkout-decisions';


// Import Zod schemas for runtime validation
import {
  CreatePaymentIntentResponseSchema,
  ShippingRatesCombinedResponseSchema,
  GuestShippingRatesResponseSchema,
} from '@/schemas/api/checkout';
import type {
  CreatePaymentIntentError,
  ShippingRatesError,
} from '@/schemas/api/checkout';
import { SetDefaultAddressResponseSchema } from '@/schemas/api/addresses';
import {
  GeocodeAddressResponseSchema,
  type ResolvedGuestAddress,
} from '@/schemas/api/checkout/geocode-address';
import { GetAllPaymentMethodsResponseSchema } from '@/schemas/api/wallet';

// Import checkout services
import { Address } from './AppSessionProvider';
import { type PaymentMethod } from '@/schemas/api/wallet';
import type { CheckoutWidgetVariant } from '@/schemas/api/admin/checkout-widget-ab';
import { widgetPreviewCookie } from '@/utils/checkout-widget-preview';
import {
  clearGuestCheckoutDraft,
  pickSavedRateIndex,
  readGuestCheckoutDraft,
  saveGuestCheckoutDraft,
} from '@/utils/guest-checkout-draft';
import {
  type Product as CheckoutProductBase,
  type ShippingRate,
  // Validation
  validateProductForCheckout,
  validateProductBasic,
  validateSellerTrustLevel,
  validateShippingSelection,
  validatePaymentMethod,
  validateGuestAddress,
  getGuestAddressMissingFields,
  // Shipping
  transformShippingRates,
  createAddressHash,
  buildAuthenticatedShippingRequest,
  buildGuestShippingRequest,
  getShippingEndpoint,
  isDimensionsError,
  findDefaultAddress,
  getSelectedShippingRate,
  // API helpers
  getListingStatusMessage,
  isSellerSetupError,
  isPaymentSuccessStatus,
  isPaymentRequiresConfirmation,
  buildPurchaseEventData,
  buildBeginCheckoutEventData,
  extractErrorMessage,
} from '../utils/checkout';
import type { RawShippingRate } from '../utils/checkout/checkout-types';
import { readAutoCoupon, codeBeatsAutoCoupon } from '../utils/checkout/price-breakdown';
import { waitForGuestOrder, rememberPurchase } from '../utils/claim-guest-order';
import { sendEmailLoginCode } from '../utils/login-code';
import { asError } from '../utils/error-message';
import { getFunnelSessionId } from '../utils/track-funnel';

interface GuestShippingAddress {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

type CheckoutProduct = CheckoutProductBase;

/** What a wallet reveals before approval, or the whole approved address. */
export interface ExpressQuoteAddress {
  name?: string;
  line1?: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface ExpressRateQuote {
  rates: ShippingRate[];
  /** The seller has no origin: the two arrange shipping and none is charged. */
  arrangedSeparately: boolean;
}

export interface ExpressOrder {
  email: string;
  shippingAddress: GuestShippingAddress;
  /** Null when shipping is arranged separately. */
  rate: ShippingRate | null;
}

/** One flat shape (strict is off, so a union would not narrow). */
export interface ExpressIntentResult {
  clientSecret?: string;
  paymentIntentId?: string;
  /** What the intent charges, in cents, when the server said. */
  amount?: number;
  /** Set on any refusal: the words to show the buyer. */
  error?: string;
}

/** What a finished order is shown with, when it did not come from the page's own form. */
/** The server's answer about a payment Stripe sent the buyer back from. */
export type StripeReturnVerdict = z.infer<typeof StripeReturnSuccessResponseSchema>;

export interface PaidOrderDetails {
  email?: string;
  shippingProviders?: ShippingRate[];
  shipping?: number;
  arrangedSeparately?: boolean;
  /**
   * How it was paid, for the `complete` event. Without it every completion
   * looked alike, and express, Cash App and card conversions could not be
   * told apart. Defaults to the card form for the buyer's sign-in state.
   */
  method?: { source: PaymentMethodSource; type?: string };
  /**
   * The buyer coupon the charge included. A buyer back from a payment that
   * left the page reloaded on the way, so the page's own coupon state is gone
   * and the receipt reads it from here (utils/checkout/stripe-return.ts).
   */
  coupon?: { discount: number; code: string; mode: 'code' | 'auto' } | null;
}

export interface ExpressPaidOrder {
  email: string;
  shippingAddress: GuestShippingAddress;
  rates: ShippingRate[];
  rate: ShippingRate | null;
  /** Stripe's `expressPaymentType`: apple_pay, google_pay, link. */
  wallet?: string;
}

const {
  noDimensionsNotification,
  requiresConfirmationMsg,
  choosePaymentMethodMsg,
  savedCardNotCompletedMsg,
} = shared.checkout;

const { setDefaultSuccessMsg: addressSetDefaultMsg } = shared.account.addresses;

interface CheckoutContextType {
  // State
  product: CheckoutProduct | null;
  /**
   * Whether the product fetch has answered -- either way.
   *
   * `product` alone cannot tell "still loading" from "there is no such
   * listing", and the order summary has to draw a placeholder for the first and
   * nothing for the second. This is the same separation browse makes with
   * `searchEmpty`: an empty result is only meaningful once something answered.
   */
  productResolved: boolean;
  /**
   * The listing whose `started` has been recorded, or null. The page's own step
   * events (email, address) wait for it: a reload with a restored guest draft
   * recorded `email_entered` and `address_entered` before `started` (prod,
   * 2026-10-26), because a child's effects run before its provider's.
   */
  startedListingId: number | string | null;
  /**
   * What this browser's saved guest draft filled in when the page opened, as
   * yes/no only (utils/checkout-form-state.ts). Never the values.
   */
  guestDraftPrefill: { email: boolean; name: boolean; address: boolean };
  shippingProviders: ShippingRate[];
  shipping: number;
  paymentMethods: PaymentMethod[];
  paymentMethodId: string | null;
  countries: Record<string, unknown>[];
  showAddCardButton: boolean;
  clientSecret: string;
  /**
   * Which payment widget the intent was created for, guest or signed in. The
   * server picks it (utils/checkout-widget-ab.ts) because the intent itself
   * differs: A (`classic`) offers every Dashboard method, B (`styled`) a set
   * list. A unless the Orders dash test is on.
   */
  widgetVariant: CheckoutWidgetVariant;
  transactionId: number;
  loadingPaymentMethods: boolean;
  loadedPaymentMethods: boolean;
  estimatePossible: boolean;
  setEstimatePossible: (possible: boolean) => void;
  /**
   * The rate route said this parcel is arranged between buyer and seller: a
   * cross-border route, or a seller with no origin. The order summary and the
   * receipt show "Arranged Separately" from this, never from an empty rate list.
   */
  shippingArrangedSeparately: boolean;
  /**
   * The last quote for this address failed: the route errored, refused, sent
   * something we could not read, or found no rate. The shipping section says so
   * and offers Try again, and Pay refuses with SHIPPING_QUOTE_FAILED_MESSAGE.
   * Before, the section simply vanished and Pay, after a card, said "Please
   * select a shipping method" over an empty list.
   */
  shippingQuoteFailed: boolean;
  /** Ask for the quote again, for the address on the page. */
  retryShippingQuote: () => void;
  step: number;
  stripePromise: Promise<Stripe | null> | null;
  contactingStripe: boolean;
  confirming: boolean;
  loadingShipping: boolean;
  loadingAddresses: boolean;

  // Guest checkout state
  guestEmailState: { guestEmail: string };
  guestEmailErrors: { guestEmail: string };
  guestEmailFocus: { guestEmail: boolean };
  guestEmailFilled: { guestEmail: boolean };
  guestShippingAddress: {
    name: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };

  // Setters
  setProduct: (product: CheckoutProduct | null) => void;
  setShipping: (index: number) => void;
  setPaymentMethodId: (id: string | null) => void;
  setShowAddCardButton: (show: boolean) => void;
  setClientSecret: (secret: string) => void;
  setStep: (step: number) => void;
  setCountries: (countries: Record<string, unknown>[]) => void;
  setGuestEmailState: (state: { guestEmail: string }) => void;
  setGuestEmailErrors: (errors: { guestEmail: string }) => void;
  setGuestEmailFocus: (focus: { guestEmail: boolean }) => void;
  setGuestEmailFilled: (filled: { guestEmail: boolean }) => void;
  setGuestShippingAddress: (address: GuestShippingAddress) => void;

  // Actions
  getShippingProviders: (id?: string | number, providedAddresses?: Address[] | null, guestAddr?: GuestShippingAddress | null) => Promise<void>;
  getPaymentMethods: () => Promise<void>;
  setDefaultAddress: (id: string) => Promise<void>;
  completeCheckout: () => Promise<void>;
  /** A guest's payment intent, made at Pay; the client secret, or null when refused (already shown). */
  prepareGuestPayment: () => Promise<string | null>;
  /** A signed-in buyer's new-card intent, made at Pay; the client secret, or null when refused (already shown). */
  prepareSignedInPayment: () => Promise<string | null>;
  /** The card form's Pay records the rate it is paying for, before the attempt. */
  recordPaidRate: () => void;
  confirmShipping: () => Promise<void>;
  addCreditCard: () => Promise<void>;
  gotoStep1: () => void;
  gotoStep2: () => void;
  popSuccess: (paymentIntentId?: string, paid?: PaidOrderDetails) => Promise<void>;
  /**
   * Express checkout (Apple Pay, Google Pay). The wallet sheet quotes and pays
   * without touching the page's own form, so these take everything they need
   * as arguments; see components/checkout/ExpressCheckout.tsx.
   */
  quoteExpressRates: (address: ExpressQuoteAddress) => Promise<ExpressRateQuote | null>;
  createExpressPaymentIntent: (order: ExpressOrder) => Promise<ExpressIntentResult>;
  finishExpressOrder: (paymentIntentId: string, paid: ExpressPaidOrder) => Promise<void>;
  getProduct: (id: string | string[]) => Promise<CheckoutProduct | null>;
  checkListingStatus: (id: string | string[]) => Promise<{ shouldContinue: boolean; statusData?: Record<string, unknown> }>;
  /**
   * What became of a payment that left the page, asked of Stripe through the
   * server. Null when the server could not say; see utils/checkout/stripe-return.ts.
   */
  verifyStripeReturn: (payment: StripeReturnParams, listingId: string) => Promise<StripeReturnVerdict | null>;
  /** Resolve a guest's typed address on the server, or null when it cannot be placed. */
  geocodeTypedAddress: (text: string) => Promise<ResolvedGuestAddress | null>;

  // Coupon state
  couponId: number | null;
  couponCode: string | null;
  couponDiscount: number;
  couponName: string;
  couponType: string;
  couponValidating: boolean;
  couponError: string;
  couponApplicationMode: 'code' | 'auto' | '';
  applyCouponCode: (code: string) => Promise<void>;
  removeCoupon: () => void;

  // Computed values
  defaultAddress: Address | null;
  selectedShipping: ShippingRate | null;
  shipAmount: number;
  subTotal: number;
  stripeFee: number;
  /** Dollars of sales tax quoted with the highlighted rate; 0 when none applies. */
  salesTax: number;
  totalPrice: number;
}

const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within CheckoutProvider');
  }
  return context;
};

export const CheckoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const couponsActive = true;
  // Use SSG-safe router context
  const router = useRouterContext();
  const { push } = router;

  const { profile, loggedIn } = useUser();
  const { createNotification } = useNotifications();
  const {
    setPopupOpen,
    setPopupContents,
    setLoginOpen,
    setLoginView,
    setLoginPrefill,
  } = useUI();
  const { addresses, createAddress } = useAppSession();
  // CJ variant from ProductProvider — `null` for non-variant listings,
  // or when CheckoutProvider is mounted outside a ProductProvider tree
  // (e.g. embed-only checkout flows or unit tests). When present, threaded
  // into shipping rate + payment intent requests so the server uses the
  // right vid for freight calc and order placement, and validates the
  // variant price against externalData.
  const productCtx = useProductOptional();
  const selectedVariant = productCtx?.selectedVariant ?? null;

  // Ref to track last fetched shipping address (prevents duplicate API calls)
  const lastFetchedShippingRef = React.useRef<string>('');
  /** Whether a quote has answered in this checkout; the next request is a re-quote. */
  const quoteAnsweredRef = React.useRef(false);

  // Ref to track if we've already shown a seller setup error (prevents spam)
  const sellerSetupErrorShownRef = React.useRef<boolean>(false);

  // Refs to guard against concurrent fetches without destabilizing useCallback identity
  const loadingShippingRef = React.useRef<boolean>(false);
  const loadingPaymentMethodsRef = React.useRef<boolean>(false);

  // Core state
  const [product, setProduct] = useState<CheckoutProduct | null>(null);
  const [productResolved, setProductResolved] = useState(false);
  const [startedListingId, setStartedListingId] = useState<number | string | null>(null);
  const [shippingProviders, setShippingProviders] = useState<ShippingRate[]>([]);
  const [shipping, setShipping] = useState<number>(0);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [countries, setCountries] = useState<Record<string, unknown>[]>([]);
  const [showAddCardButton, setShowAddCardButton] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  const [widgetVariant, setWidgetVariant] = useState<CheckoutWidgetVariant>('classic');
  const [transactionId, _setTransactionId] = useState(0);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [loadedPaymentMethods, setLoadedPaymentMethods] = useState(false);
  const [estimatePossible, setEstimatePossible] = useState(true);
  // The quote said this parcel is arranged between buyer and seller: a
  // cross-border route, or a seller with no origin. Kept apart from
  // `estimatePossible`, which also goes false when a quote simply FAILED -- that
  // case must still block checkout rather than ship for free.
  const [shippingArrangedSeparately, setShippingArrangedSeparately] = useState(false);
  const [shippingQuoteFailed, setShippingQuoteFailed] = useState(false);
  const [step, setStep] = useState(1);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [contactingStripe, setContactingStripe] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [loadingAddresses, _setLoadingAddresses] = useState(false);

  // Track first interaction

  // Guest checkout state.
  //
  // Seeded from what this browser typed last time (utils/guest-checkout-draft.ts)
  // so a reload does not start a guest from scratch. Read once, in the initial
  // state rather than an effect: the guest form mounts with these as its own
  // starting values and pushes its fields back up, so a draft that arrived a
  // render later would be overwritten by the empty fields it mounted with. The
  // form only renders once `clientSide` is set, so the server markup never
  // depends on it.
  const [initialGuestDraft] = useState(() => readGuestCheckoutDraft());
  const [guestDraftPrefill] = useState(() => ({
    email: Boolean(initialGuestDraft?.email?.trim()),
    name: Boolean(initialGuestDraft?.address?.name?.trim()),
    address: Boolean(initialGuestDraft?.address?.line1?.trim()),
  }));
  const [guestEmailState, setGuestEmailState] = useState({ guestEmail: initialGuestDraft?.email ?? '' });
  const [guestEmailErrors, setGuestEmailErrors] = useState({ guestEmail: '' });
  const [guestEmailFocus, setGuestEmailFocus] = useState({ guestEmail: false });
  const [guestEmailFilled, setGuestEmailFilled] = useState({ guestEmail: !!initialGuestDraft?.email });
  const [guestShippingAddress, setGuestShippingAddress] = useState(
    initialGuestDraft?.address ?? {
      name: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  );

  // Coupon state
  const [couponId, setCouponId] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponName, setCouponName] = useState('');
  const [couponType, setCouponType] = useState('');
  const [couponApplicationMode, setCouponApplicationMode] = useState<'code' | 'auto' | ''>('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Computed values - using service functions
  const defaultAddress = findDefaultAddress(addresses);

  const selectedShipping = getSelectedShippingRate(shippingProviders, shipping) || shippingProviders[0];

  // The rate selected now goes on any abandonment that follows, so a buyer who
  // leaves after picking Priority over Ground is recorded with what they saw.
  React.useEffect(() => {
    const fields = buildSelectedRateFields(selectedShipping);
    if (fields) updateAbandonmentMetadata('checkout', fields);
  }, [selectedShipping]);

  // Totals (ship amount, coupon deduction, Stripe fee) are pure arithmetic —
  // see utils/checkout-decisions.ts. Display only: the server re-derives every
  // amount it charges.
  const { shipAmount, subTotal, stripeFee, salesTax, totalPrice } = computeCheckoutTotals({
    productPrice: product?.price,
    // The listing's automatic coupon rides on it as flat keys; the totals use
    // the same sum the order summary draws its rows from.
    listing: product,
    // Every listing that checks out ships, so none has its shipping zeroed.
    isVehicleListing: false,
    selectedShippingAmount: selectedShipping?.amount,
    selectedShippingTaxCents: selectedShipping?.tax?.taxCents,
    couponType,
    couponDiscount,
    couponCode,
    couponApplicationMode,
  });

  // The listing's automatic coupon, when it has one. A typed code is worked
  // out against the price BEFORE it, because that is what the server
  // validates a code against (see create-payment-intent's coupon block).
  const autoCoupon = React.useMemo(() => readAutoCoupon(product), [product]);
  const codeBasePrice = autoCoupon ? autoCoupon.originalPrice : Number(product?.price);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- deps intentionally key on the specific product fields used (price via codeBasePrice, id, user.id) so this checkout callback is not recreated on unrelated product reference changes; broadening to `product` would recreate it (and re-run the URL-coupon effect) more frequently
  const applyCouponCode = useCallback(async (code: string) => {
    if (!couponsActive || !product?.id || !codeBasePrice) return;
    setCouponValidating(true);
    setCouponError('');
    try {
      const resp = await fetch('/api/checkout/validate-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          itemPrice: codeBasePrice,
          listingId: Number(product.id),
          sellerId: Number(product.user?.id),
        }),
      });
      const data = await resp.json();
      if (data.success && !codeBeatsAutoCoupon(autoCoupon, { couponId: data.couponId, discountAmount: data.discountAmount })) {
        // One coupon moves the price, and a code replaces the automatic one.
        // A code worth less would put the price above the one shown, which the
        // server refuses, so the automatic discount stays.
        createNotification({
          type: 'info',
          msg: data.couponId === autoCoupon?.couponId
            ? 'That discount is already applied.'
            : `This listing's ${autoCoupon?.label} discount is already more than that code, so it stays.`,
          dismissed: false,
        });
      } else if (data.success) {
        setCouponId(data.couponId);
        setCouponCode(data.code);
        setCouponDiscount(data.discountAmount);
        setCouponName(data.name);
        setCouponType(data.couponType);
        setCouponApplicationMode('code');
      } else {
        createNotification({ type: 'error', msg: data.error || 'Invalid coupon code', dismissed: false });
      }
    } catch {
      createNotification({ type: 'error', msg: 'Failed to validate coupon', dismissed: false });
    }
    setCouponValidating(false);
  }, [product?.id, product?.user?.id, codeBasePrice, autoCoupon, createNotification, couponsActive]);

  /**
   * The guest address box's fallback for when the Maps script never loaded.
   *
   * Every failure (refused, rate limited, offline, a body that doesn't match the
   * contract) comes back as null, because the component answers them all the
   * same way: by asking for the address fields one at a time.
   */
  const geocodeTypedAddress = useCallback(async (text: string): Promise<ResolvedGuestAddress | null> => {
    try {
      const resp = await fetch('/api/checkout/geocode-address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: text }),
      });
      const parsed = GeocodeAddressResponseSchema.safeParse(await resp.json());
      if (!parsed.success) {
        logErrorEvent(new Error('geocode-address response did not match its contract'), ErrorSeverity.WARNING, {
          component: 'CheckoutProvider',
          action: 'geocodeTypedAddress',
          extra: { status: resp.status },
        });
        return null;
      }
      return parsed.data.success ? parsed.data.address : null;
    } catch (error) {
      // Offline or a non-JSON body. The buyer is asked for the fields either way.
      logErrorEvent(asError(error), ErrorSeverity.WARNING, {
        component: 'CheckoutProvider',
        action: 'geocodeTypedAddress',
      });
      return null;
    }
  }, []);

  const removeCoupon = useCallback(() => {
    setCouponId(null);
    setCouponCode(null);
    setCouponDiscount(0);
    setCouponName('');
    setCouponType('');
    setCouponApplicationMode('');
    setCouponError('');
  }, []);

  // Read coupon from URL params on product load
  React.useEffect(() => {
    if (!couponsActive || !product?.id) return;
    const params = new URLSearchParams(window.location.search);
    const urlCouponCode = params.get('coupon');
    if (urlCouponCode && !couponId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- triggers an async coupon-validation fetch when a `?coupon=` URL param is present on product load; this is an external async action (its setState happens later in the awaited callback), not state derivable during render
      void applyCouponCode(urlCouponCode);
    }
  }, [product?.id, couponId, applyCouponCode, couponsActive]);

  // Auto-apply discounts are resolved server-side in create-payment-intent.
  //
  // There used to be an effect here seeding coupon state from
  // `product.autoApplyCoupon`. Nothing has ever produced that field —
  // /api/listings/[id] emits the coupon as flat `couponId`/`couponName` keys —
  // so it never ran, `couponId` stayed null, and no redemption was recorded for
  // any auto-apply coupon. The server now re-derives the discount from the same
  // resolver the product page uses, which both applies it to the charge and
  // records the redemption its usage limit depends on.

  // Create payment intent helper
  const createPaymentIntent = async (
    listing: CheckoutProduct,
    paymentMethodId?: string,
    shippingAddress?: Address | null,
    shippingRate?: ShippingRate | null,
  ) => {
    // Validate required fields using service
    const productValidation = validateProductForCheckout(listing);
    if (!productValidation.valid) {
      throw new Error('Missing required listing information');
    }

    // CJ variant: when the buyer picked a variant, send the variant id and
    // the variant's price. The server validates the price against
    // LISTINGS_METADATA.externalData.variants — the value here is purely
    // for client-side display continuity, the server is the source of truth.
    try {
      const response = await fetch('/api/checkout/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(buildAuthenticatedPaymentIntentBody({
          listing,
          paymentMethodId,
          shippingAddress,
          shippingRate,
          // Only set when the buyer had no email on file and typed one into the
          // checkout email field; the server prefers the account's own.
          email: guestEmailState.guestEmail || undefined,
          selectedVariant,
          couponsActive,
          couponApplicationMode,
          couponId,
          couponCode,
        })),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.details) {
          logErrorEvent(new Error('Payment intent validation errors'), ErrorSeverity.ERROR, { action: 'createPaymentIntent', component: 'CheckoutProvider', metadata: { details: errorData.details } } as ErrorContext);
        }

        // Handle listing status errors using service
        if (errorData.listingStatus && errorData.listingStatus !== 1) {
          const statusMessage = getListingStatusMessage(errorData.listingStatus);

          createNotification({
            type: "error",
            msg: statusMessage,
            dismissed: false,
          });
          trackError('checkout', 'checkout_redirect', statusMessage, {
            listing_id: listing?.id, listing_status: errorData.listingStatus,
            redirect_to: `/product/${listing?.id}`, step: 'payment_intent_creation',
          });
          logErrorEvent(statusMessage, ErrorSeverity.WARNING, {
            component: 'checkout', action: 'checkout_redirect', listing_id: Number(listing?.id),
            funnel_type: 'checkout', funnel_step: 'payment_intent_creation',
            tags: { listing_status: String(errorData.listingStatus) },
          });

          setTimeout(() => {
            push(`/product/${listing?.id}`);
          }, 100);

          return null;
        }

        // Handle seller Stripe setup errors using service
        if (isSellerSetupError(errorData.error)) {
          // Only show notification if we haven't already
          if (!sellerSetupErrorShownRef.current) {
            sellerSetupErrorShownRef.current = true;
            createNotification({
              type: "error",
              msg: 'This seller has not completed their payout account setup. Checkout is not available.',
              dismissed: false,
            });
            trackError('checkout', 'checkout_redirect', 'Seller has not completed payout account setup', {
              listing_id: listing?.id, redirect_to: `/product/${listing?.id}`, step: 'payment_intent_creation',
            });
            logErrorEvent('Seller has not completed payout account setup', ErrorSeverity.WARNING, {
              component: 'checkout', action: 'checkout_redirect', listing_id: Number(listing?.id),
              funnel_type: 'checkout', funnel_step: 'payment_intent_creation',
            });

            setTimeout(() => {
              push(`/product/${listing?.id}`);
            }, 100);
          }

          return null;
        }

        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const rawData = await response.json();

      // Validate response with Zod schema
      const parseResult = CreatePaymentIntentResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        logErrorEvent(new Error('createPaymentIntent validation failed'), ErrorSeverity.ERROR, { action: 'createPaymentIntent', component: 'CheckoutProvider', metadata: { zodErrors: parseResult.error.flatten() } } as ErrorContext);
        throw new Error('Invalid response format from payment intent API');
      }

      const data = parseResult.data;

      if (!data.success) {
        const errData = data as CreatePaymentIntentError;
        if (errData.couponFailed) {
          removeCoupon();
          createNotification({ type: 'error', msg: errData.error, dismissed: false });
          return null;
        }

        // The server refused to bill more than this page displayed. Clear the
        // stale coupon state and reload so the buyer sees the real total before
        // deciding again — throwing here would show a failure for what is
        // really a changed price.
        if (errData.priceChanged) {
          removeCoupon();
          createNotification({ type: 'error', msg: errData.error, dismissed: false });
          trackError('checkout', 'price_changed', errData.error, {
            listing_id: Number(listing.id),
            new_item_price: errData.itemPrice,
            step: 'payment_intent_creation',
          });
          return null;
        }

        // The chosen rate no longer verifies with the carrier. Drop it so the
        // buyer re-picks from a fresh quote instead of retrying a dead rate.
        if (errData.shippingRateExpired) {
          // Drop back to the first quoted option so the buyer re-picks rather
          // than retrying a rate the carrier no longer honours.
          setShipping(0);
          createNotification({ type: 'error', msg: errData.error, dismissed: false });
          trackError('checkout', 'shipping_rate_expired', errData.error, {
            listing_id: Number(listing.id),
            step: 'payment_intent_creation',
          });
          return null;
        }

        throw new Error(errData.error || 'Failed to create payment intent');
      }

      if (data.pendingTransaction) {
        trackError('checkout', 'checkout_redirect', 'Pending transaction already exists', {
          listing_id: data.listingId, redirect_to: `/account/purchases/${data.listingId}`,
          step: 'payment_intent_creation',
        });
        logErrorEvent('Pending transaction already exists', ErrorSeverity.INFO, {
          component: 'checkout', action: 'checkout_redirect', listing_id: Number(data.listingId),
          funnel_type: 'checkout', funnel_step: 'payment_intent_creation',
        });
        window.location.href = `/account/purchases/${data.listingId}`;
        return null;
      }

      if (!data.clientSecret) {
        throw new Error('Invalid response from payment intent API');
      }

      setWidgetVariant(data.widgetVariant ?? 'classic');
      setClientSecret(data.clientSecret);
      return {
        paymentIntentId: data.paymentIntentId,
        clientSecret: data.clientSecret,
        status: data.status,
      };
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error creating payment intent'), ErrorSeverity.ERROR, { action: 'createPaymentIntent', component: 'CheckoutProvider' });
      createNotification({
        type: "error",
        msg: errorMessageOr(error, 'Failed to create payment intent'),
        dismissed: false,
      });
      return null;
    }
  };

  // Fetch product from search index
  const getProduct = useCallback(async (id: string | string[]) => {
    try {
      const fetchedProduct = await fetchSearchListItem(id as string);
      setProduct(fetchedProduct);
      return fetchedProduct;
    } finally {
      // In `finally` so a thrown fetch still ends the loading state. Otherwise
      // the order summary placeholder shimmers forever on the one path where
      // there is definitively nothing to show.
      setProductResolved(true);
    }
  }, []);

  // Check listing status and seller verification before checkout
  const checkListingStatus = useCallback(async (id: string | string[]) => {
    try {
      const statusCheck = await fetch(`/api/listings/${id}/status`);
      if (statusCheck.ok) {
        const statusData = await statusCheck.json();
        return { shouldContinue: true, statusData };
      }
    } catch (error) {
      // A bare "Failed to fetch" here cannot say on its own whether the buyer
      // left the page mid-request or our route was unreachable. On 2026-10-20
      // it fired in the same millisecond as the page's similar-listings
      // preload, which is what a navigation cutting both looks like. The
      // transport snapshot settles it, and a request the page abandoned files
      // at info instead of queueing as a warning.
      const transport = transportFailureContext(error);
      logErrorEvent(
        errorMessageOr(error, 'Error checking listing status'),
        severityForOwnFetchFailure(error, ErrorSeverity.WARNING, transport),
        {
          component: 'CheckoutProvider', action: 'checkListingStatus', listing_id: Number(id),
          extra: { ...transport },
        }
      );
      // Continue to load product - API will validate again
    }
    return { shouldContinue: true };
  }, []);

  const verifyStripeReturn = useCallback(async (payment: StripeReturnParams, listingId: string): Promise<StripeReturnVerdict | null> => {
    try {
      const response = await fetch('/api/checkout/stripe-return', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payment, listingId }),
      });
      // safeParse: a response shape from a newer or older deploy is "could not
      // say", never a thrown error on the page the buyer just paid on.
      const parsed = StripeReturnSuccessResponseSchema.safeParse(await response.json());
      if (parsed.success) return parsed.data;
      logErrorEvent(`Stripe return check answered ${response.status}`, ErrorSeverity.WARNING, {
        component: 'CheckoutProvider', action: 'verifyStripeReturn', listing_id: Number(listingId),
      });
    } catch (error) {
      logErrorEvent(errorMessageOr(error, 'Stripe return check failed'), ErrorSeverity.WARNING, {
        component: 'CheckoutProvider', action: 'verifyStripeReturn', listing_id: Number(listingId),
      });
    }
    return null;
  }, []);

  /**
   * A quote that failed leaves nothing to buy.
   *
   * The rates and the arranged-separately flag from the LAST address used to
   * survive a failed quote for the next one, so a buyer who changed address and
   * got a refusal still had the old address's rate selected (and could pay for
   * it), or an old "arranged separately" that let checkout through with no
   * shipping at all. Both are cleared, and the summary reads "No Shipping".
   */
  const clearShippingQuote = useCallback(() => {
    setShippingProviders([]);
    setShippingArrangedSeparately(false);
    setEstimatePossible(false);
  }, []);

  // Get shipping providers - refactored to use services
  const getShippingProviders = useCallback(async (id?: string | number, providedAddresses: Address[] | null = null, guestAddr: GuestShippingAddress | null = null) => {
    if (loadingShippingRef.current) return;
    if (!id) id = product?.id;

    let currentDefaultAddress = null;
    const isGuestCheckout = !!guestAddr;

    if (guestAddr) {
      // Validate required fields for guest address using service
      const guestValidation = validateGuestAddress(guestAddr);
      if (!guestValidation.valid) {
        logErrorEvent('Guest address missing required fields', ErrorSeverity.WARNING, {
          component: 'CheckoutProvider',
          action: 'fetchShippingRates',
          extra: { missingFields: getGuestAddressMissingFields(guestAddr) },
        });
        return;
      }
      currentDefaultAddress = guestAddr;
    } else {
      const addressesToUse = providedAddresses || addresses;
      currentDefaultAddress = findDefaultAddress(addressesToUse);
    }

    if (!id || !currentDefaultAddress) return;

    // Create a hash to check if we've already fetched for this address using service
    const addressHash = createAddressHash(id, currentDefaultAddress, isGuestCheckout);

    // Prevent duplicate fetches for the same address
    if (lastFetchedShippingRef.current === addressHash) {
      return;
    }

    lastFetchedShippingRef.current = addressHash;
    loadingShippingRef.current = true;
    setLoadingShipping(true);
    setShippingQuoteFailed(false);
    // A failed quote may be asked again (retryShippingQuote) for the same address.
    const quoteFailed = () => {
      lastFetchedShippingRef.current = '';
      setShippingQuoteFailed(true);
    };

    /**
     * The quote was asked for. This is the denominator the checkout funnel never
     * had.
     *
     * Only the success path below emitted anything, so a quote that failed --
     * schema mismatch, the server refusing, zero rates, a thrown fetch -- left
     * no trace in `funnel_events` at all, and the session looked identical to
     * one where the shopper simply never got that far. On production over
     * 2026-09-08..15 that is the shape of the whole funnel: 988 sessions reached
     * /checkout and 31 recorded a `shipping_calculated`, with no way to tell how
     * many of the other 957 asked and were refused. 351 `Seller has no address
     * and no location; cannot quote shipping` sat in `error_logs` over the same
     * week, unattributable to any of them.
     */
    // A second quote in the same checkout means the address changed after the
    // buyer saw the first one -- worth knowing about anyone who then left.
    const afterQuote = quoteAnsweredRef.current;
    trackFunnelEvent('checkout', 'shipping_quote_requested', {
      listing_id: Number(id),
      is_guest: isGuestCheckout,
      ...(afterQuote ? { after_quote: true } : {}),
    });
    if (afterQuote) updateAbandonmentMetadata('checkout', { address_changed_after_quote: true });

    /**
     * What the quote said, for its own row and for an abandonment after it.
     * Before 2026-10-26 a buyer who left after shipping left only a step name:
     * $6 on a $35 top, $46 on a $15 record and no rates at all looked the same.
     */
    const quoteStartedAt = performance.now();
    const quoteResult = (outcome: ShippingQuoteOutcome, rates?: ShippingRate[], error?: string) => {
      quoteAnsweredRef.current = true;
      const result = buildShippingQuoteResult({
        outcome, rates, error, itemPrice: product?.price, quoteMs: performance.now() - quoteStartedAt,
      });
      updateAbandonmentMetadata('checkout', result);
      return result;
    };

    /**
     * Advance the step an abandonment would report.
     *
     * `updateAbandonmentStep` was called in exactly one place -- the move to the
     * payment step -- which 9 sessions in 14 days ever reached. Every one of the
     * other 742 checkout abandonments therefore reported `event_step: "started"`,
     * so the single most useful thing about an abandonment, WHERE the shopper
     * gave up, was the same constant on every row.
     */
    updateAbandonmentStep('checkout', 'shipping_quote_requested', { listing_id: Number(id) });

    try {
      // Use service functions for endpoint and request body. Thread the CJ
      // variant id when one is selected — server-side branches in
      // shipping-rates(-guest).ts use it to call CJ's freightCalculate with
      // the variant-specific dimensions.
      const endpoint = getShippingEndpoint(isGuestCheckout);
      const variantVid = selectedVariant?.vid;

      const requestBody = isGuestCheckout
        ? buildGuestShippingRequest(id, currentDefaultAddress, variantVid)
        : buildAuthenticatedShippingRequest(id, variantVid);

      const ratesResponse = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      const rawRatesData = await ratesResponse.json();

      // Validate response with appropriate Zod schema
      const ResponseSchema = isGuestCheckout
        ? GuestShippingRatesResponseSchema
        : ShippingRatesCombinedResponseSchema;
      const parseResult = ResponseSchema.safeParse(rawRatesData);

      if (!parseResult.success) {
        logErrorEvent(new Error('getShippingProviders validation failed'), ErrorSeverity.ERROR, { action: 'getShippingProviders', component: 'CheckoutProvider', metadata: { zodErrors: parseResult.error.flatten() } } as ErrorContext);
        trackFunnelEvent('checkout', 'shipping_quote_failed', {
          listing_id: Number(id),
          is_guest: isGuestCheckout,
          reason: 'malformed_response',
          status: ratesResponse.status,
          ...quoteResult('error', [], 'malformed_response'),
        });
        clearShippingQuote();
        quoteFailed();
        setLoadingShipping(false);
        return;
      }

      const ratesData = parseResult.data;

      if (!ratesData.success) {
        // Use service to check for dimensions error
        const refusal = (ratesData as ShippingRatesError).error;
        const missingDimensions = isDimensionsError(refusal);
        if (missingDimensions) {
          createNotification({
            type: "error",
            msg: noDimensionsNotification,
            dismissed: false,
          });
        }
        // The refusal reason is the whole point of the row: `no_dimensions` is a
        // seller who never entered a box size, and anything else is usually the
        // seller having no shipping origin at all. Those are different problems
        // with different fixes, and the funnel could not tell them apart.
        trackFunnelEvent('checkout', 'shipping_quote_failed', {
          listing_id: Number(id),
          is_guest: isGuestCheckout,
          reason: missingDimensions ? 'no_dimensions' : 'refused',
          // Both refusal schemas require `error` as a string, so it is always here.
          message: refusal.slice(0, 200),
          ...quoteResult('error', [], missingDimensions ? 'no_dimensions' : 'refused'),
        });
        clearShippingQuote();
        quoteFailed();
        setLoadingShipping(false);
        return;
      }

      if (ratesData.rates && ratesData.rates.length > 0) {
        // Use service to transform rates
        const transformedRates = transformShippingRates(ratesData.rates as RawShippingRate[], ratesData.shipmentId);

        setShippingProviders(transformedRates);
        // A guest who picked a rate before reloading gets the same one back,
        // matched by carrier and service; anyone else gets the first.
        setShipping(
          isGuestCheckout ? pickSavedRateIndex(transformedRates, readGuestCheckoutDraft()?.shipping, id) : 0,
        );
        setShippingArrangedSeparately(false);

        // Track shipping rates calculated
        trackFunnelEvent('checkout', 'shipping_calculated', {
          ...buildShippingCalculatedEventData(id, transformedRates),
          ...quoteResult('rates', transformedRates),
        });
        updateAbandonmentStep('checkout', 'shipping_calculated', {
          listing_id: Number(id),
          rate_count: transformedRates.length,
        });
      } else if (ratesData.arrangedSeparately) {
        // Not a failure: the server is telling us this parcel cannot be priced
        // by anyone -- another country, or a seller with no origin -- so the two
        // of them arrange it and checkout charges nothing for shipping. Without
        // this the buyer was asked to "select a shipping method" from an empty
        // list and could not get past it, which is how cross-border checkout
        // broke silently when that path started returning no rates.
        setShippingProviders([]);
        setShippingArrangedSeparately(true);
        setEstimatePossible(false);
        quoteResult('arranged_separately');
      } else {
        // A successful call that carries no rates. Distinct from a refusal: the
        // quote worked and nothing could ship to this address.
        trackFunnelEvent('checkout', 'shipping_quote_failed', {
          listing_id: Number(id),
          is_guest: isGuestCheckout,
          reason: 'no_rates',
          ...quoteResult('no_rates', []),
        });
        clearShippingQuote();
        quoteFailed();
      }
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error fetching shipping rates'), ErrorSeverity.ERROR, { action: 'getShippingProviders', component: 'CheckoutProvider' });
      trackFunnelEvent('checkout', 'shipping_quote_failed', {
        listing_id: Number(id),
        is_guest: isGuestCheckout,
        reason: 'request_failed',
        message: extractErrorMessage(error, 'Failed to load shipping options').slice(0, 200),
        ...quoteResult('error', [], 'request_failed'),
      });
      // Reset the ref on error so user can retry
      quoteFailed();
      // Don't show error notification for guest checkout - it's expected to fail sometimes
      // Only log to console for debugging
      if (!isGuestCheckout) {
        createNotification({
          type: "error",
          msg: extractErrorMessage(error, 'Failed to load shipping options'),
          dismissed: false,
        });
      }
      clearShippingQuote();
    } finally {
      loadingShippingRef.current = false;
      setLoadingShipping(false);
    }
  }, [product, addresses, createNotification, selectedVariant, clearShippingQuote]);

  /** Try again on the failed-quote message: the same address, asked afresh. */
  const retryShippingQuote = useCallback(() => {
    lastFetchedShippingRef.current = '';
    if (loggedIn) void getShippingProviders(product?.id, addresses);
    else void getShippingProviders(product?.id, null, guestShippingAddress);
  }, [loggedIn, product?.id, addresses, guestShippingAddress, getShippingProviders]);

  /**
   * Rates for an address the wallet sheet sent, without touching the page.
   *
   * The page's own quote (getShippingProviders) writes the rate list, the
   * selected rate and the funnel's shipping steps, which belong to the form the
   * buyer can see. The sheet is a separate conversation with its own address,
   * and quoting it through the page would repaint the order summary behind the
   * wallet with an address the buyer never typed.
   */
  const expressListingId = product?.id;
  const expressVariantVid = selectedVariant?.vid;
  const quoteExpressRates = useCallback(async (address: ExpressQuoteAddress): Promise<ExpressRateQuote | null> => {
    const productId = expressListingId;
    if (!productId) return null;
    try {
      const response = await fetch(getShippingEndpoint(true), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(buildGuestShippingRequest(productId, {
          name: address.name || '',
          line1: address.line1 || '',
          line2: address.line2 || '',
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
          country: address.country || 'US',
        }, expressVariantVid)),
      });
      const parsed = GuestShippingRatesResponseSchema.safeParse(await response.json());
      if (!parsed.success || !parsed.data.success) {
        trackFunnelEvent('checkout', 'express_quote_failed', {
          listing_id: Number(productId),
          reason: parsed.success ? 'refused' : 'malformed_response',
          status: response.status,
        });
        return null;
      }
      const data = parsed.data;
      return {
        rates: transformShippingRates(data.rates as RawShippingRate[], data.shipmentId),
        arrangedSeparately: data.arrangedSeparately === true,
      };
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error quoting express shipping'), ErrorSeverity.ERROR, {
        action: 'quoteExpressRates',
        component: 'CheckoutProvider',
      });
      return null;
    }
  }, [expressListingId, expressVariantVid]);

  /**
   * The payment intent for a wallet purchase: the same guest route and the same
   * body the card form sends, flagged `express` so the server builds the
   * intent the wallet's Elements group can confirm.
   *
   * Never navigates or notifies. The wallet sheet is still open, and it is the
   * sheet that has to be told the payment failed and why.
   */
  const createExpressPaymentIntent = async (order: ExpressOrder): Promise<ExpressIntentResult> => {
    if (!product) return { error: 'This item is no longer available.' };
    try {
      // Signed in: the order belongs on the account, so it goes through the
      // signed-in route, which ships to a saved address. The wallet's address
      // is filed in the address book first (an identical one is reused).
      // Through the guest route it became a guest order under the wallet's
      // email, which is why the buttons were guest-only (owner, 2026-11-03).
      let body: unknown;
      if (loggedIn) {
        const request = toAddressBookRequest(order.shippingAddress);
        if (!request) return { error: 'We can only ship to addresses in the United States.' };
        const saved = await createAddress(request);
        if (!saved.success) {
          trackFunnelEvent('checkout', 'express_address_save_failed', { listing_id: Number(product.id) });
          return { error: 'We could not save that address. Please try again.' };
        }
        body = buildAuthenticatedPaymentIntentBody({
          listing: product,
          shippingAddress: { id: saved.address.id },
          shippingRate: order.rate,
          selectedVariant,
          couponsActive,
          couponApplicationMode,
          couponId,
          couponCode,
          // Only read by the server for an account with no email of its own.
          email: order.email,
          express: true,
        });
      }
      const response = await fetch('/api/checkout/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? buildGuestPaymentIntentBody({
          product,
          email: order.email,
          shippingAddress: { ...order.shippingAddress },
          shippingRate: order.rate,
          selectedVariant,
          couponsActive,
          couponApplicationMode,
          couponId,
          couponCode,
          partnerCode: getPartner(),
          express: true,
        })),
      });
      const json: unknown = await response.json();
      // The signed-in route answers a second order for a listing this buyer is
      // already waiting on with `pendingTransaction` and no `error`, which the
      // response schema does not take.
      if (json && typeof json === 'object' && (json as { pendingTransaction?: unknown }).pendingTransaction === true) {
        return { error: 'You already have an order waiting on this item.' };
      }
      const parsed = CreatePaymentIntentResponseSchema.safeParse(json);
      if (!parsed.success) {
        logErrorEvent(new Error('createExpressPaymentIntent validation failed'), ErrorSeverity.ERROR, {
          action: 'createExpressPaymentIntent',
          component: 'CheckoutProvider',
          metadata: { zodErrors: parsed.error.flatten() },
        } as ErrorContext);
        return { error: 'Something went wrong. Please try again.' };
      }
      const data = parsed.data;
      if (!data.success) {
        const refusal = data as CreatePaymentIntentError;
        if (refusal.listingStatus && refusal.listingStatus !== 1) {
          return { error: getListingStatusMessage(refusal.listingStatus) };
        }
        return { error: refusal.error || 'Something went wrong. Please try again.' };
      }
      if (!data.clientSecret) return { error: 'Something went wrong. Please try again.' };
      return { clientSecret: data.clientSecret, paymentIntentId: data.paymentIntentId, amount: data.amount };
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error creating express payment intent'), ErrorSeverity.ERROR, {
        action: 'createExpressPaymentIntent',
        component: 'CheckoutProvider',
      });
      return { error: extractErrorMessage(error, 'Something went wrong. Please try again.') };
    }
  };

  // Get payment methods
  const getPaymentMethods = useCallback(async () => {
    if (!loggedIn) return;
    if (loadingPaymentMethodsRef.current) return;

    loadingPaymentMethodsRef.current = true;
    setLoadingPaymentMethods(true);

    try {
      const response = await fetch('/api/wallet/payment-methods/all', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const rawData = await response.json();
      loadingPaymentMethodsRef.current = false;
      setLoadingPaymentMethods(false);

      // Validate response with Zod schema
      const parseResult = GetAllPaymentMethodsResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        logErrorEvent(new Error('getPaymentMethods validation failed'), ErrorSeverity.ERROR, { action: 'getPaymentMethods', component: 'CheckoutProvider', metadata: { zodErrors: parseResult.error.flatten() } } as ErrorContext);
        createNotification({
          type: "error",
          msg: 'Failed to load payment methods',
          dismissed: false,
        });
        return;
      }

      const data = parseResult.data;

      if (!data.success) {
        createNotification({
          type: "error",
          msg: (data as { error?: string }).error || 'Failed to load payment methods',
          dismissed: false,
        });
        return;
      }

      const methods = data.paymentMethods || [];
      setPaymentMethods(methods);
      if (methods.length) {
        setPaymentMethodId(methods[0].id);
        setShowAddCardButton(true);
      } else {
        setPaymentMethodId(null);
        // When no payment methods exist, automatically show the add card form
        setShowAddCardButton(false);
      }
      setLoadedPaymentMethods(true);
    } catch (error) {
      logErrorEvent(toTrackedError(error, 'Error fetching payment methods'), ErrorSeverity.ERROR, { action: 'getPaymentMethods', component: 'CheckoutProvider' });
      loadingPaymentMethodsRef.current = false;
      setLoadingPaymentMethods(false);
      createNotification({
        type: "error",
        msg: 'Failed to load payment methods',
        dismissed: false,
      });
    }
  }, [loggedIn, createNotification]);

  // Set default address
  const setDefaultAddress = async (id: string) => {
    try {
      const response = await fetch(`/api/addresses/${id}/set-default`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const rawData = await response.json();

      // Validate response with Zod schema
      const parseResult = SetDefaultAddressResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        logErrorEvent(new Error('setDefaultAddress validation failed'), ErrorSeverity.ERROR, { action: 'setDefaultAddress', component: 'CheckoutProvider', metadata: { zodErrors: parseResult.error.flatten() } } as ErrorContext);
        createNotification({
          type: "error",
          msg: 'Failed to set default address',
          dismissed: false
        });
        return;
      }

      const data = parseResult.data;

      if (data.success) {
        emit('afterAddressUpdated');
        if (addresses.length) await getShippingProviders(product?.id);
        createNotification({ type: "success", msg: addressSetDefaultMsg, dismissed: false });
      } else {
        createNotification({
          type: "error",
          msg: (data as { error?: string }).error || 'Failed to set default address',
          dismissed: false
        });
      }
    } catch (error) {
      logErrorEvent(toTrackedError(error, 'Error setting default address'), ErrorSeverity.ERROR, { action: 'setDefaultAddress', component: 'CheckoutProvider' });
      createNotification({
        type: "error",
        msg: 'Failed to set default address',
        dismissed: false
      });
    }
  };

  // Initialize Stripe
  const initStripe = () => {
    // No test-mode assistant badge: it floats over the pay button on a phone.
    return loadStripeSafely(WhichKey(), { developerTools: { assistant: { enabled: false } } }, 'CheckoutProvider');
  };

  // Add credit card - refactored to use validation services
  const addCreditCard = async () => {
    if (!loggedIn) return;

    // Prevent repeated error spam if we've already shown the seller setup error
    if (sellerSetupErrorShownRef.current) {
      return;
    }

    // Validate required data using service
    const productValidation = validateProductBasic(product);
    if (!productValidation.valid) {
      createNotification({
        type: "error",
        msg: productValidation.error || 'Product information is missing',
        dismissed: false
      });
      return;
    }

    // Check if seller has completed Stripe setup using validation service
    const sellerValidation = validateSellerTrustLevel(product);
    if (!sellerValidation.valid) {
      sellerSetupErrorShownRef.current = true;
      createNotification({
        type: "error",
        msg: sellerValidation.error!,
        dismissed: false
      });
      return;
    }

    setPaymentMethodId(null);
    setContactingStripe(true);

    try {
      // Get the selected shipping rate if available
      const selectedShippingRate = selectShippingRateAtIndex(shippingProviders, shipping);

      const paymentIntentResponse = await createPaymentIntent(
        product,
        undefined,
        defaultAddress || null,
        selectedShippingRate,
      );

      if (!paymentIntentResponse) {
        setContactingStripe(false);
        return;
      }

      const s = initStripe();
      setStripePromise(s);
      setShowAddCardButton(false); // Show the Stripe form
      setContactingStripe(false);
      googleEvent("checkout_add_card");
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error creating payment intent'), ErrorSeverity.ERROR, { action: 'addCard', component: 'CheckoutProvider' });
      createNotification({
        type: "error",
        msg: errorMessageOr(error, 'Failed to create payment intent'),
        dismissed: false
      });
      setContactingStripe(false);
    }
  };

  /**
   * The rate the buyer is paying for, recorded at Pay. The one-step page has no
   * "Continue" to record it at (confirmShipping, the old step change), and the
   * funnel's spine runs Shipping Calc -> Shipping Selected -> Payment Attempted.
   */
  const recordShippingSelected = () => {
    const rate = shippingProviders[shipping];
    googleEvent("begin_checkout", buildBeginCheckoutEventData(product));
    updateAbandonmentStep('checkout', 'shipping_selected', buildShippingSelectedAbandonmentContext(product, profile?.id, rate));
    trackFunnelEvent(
      'checkout',
      'shipping_selected',
      buildShippingSelectedEventData(product, rate, getCheckoutSessionId()),
      buildFunnelUserOptions(profile?.id),
    );
  };

  /**
   * The card form's Pay: the rate being paid for, recorded before the form
   * records `payment_attempted`, so the funnel and the abandonment step run
   * Shipping Selected -> Payment Attempted, as the saved-card Pay does.
   * Silent without a valid rate; the prepare call that follows says why.
   *
   * `shipping_selected` once fired from inside prepareGuestPayment, AFTER the
   * attempt: a declined buyer who left was logged as leaving at shipping, and a
   * Pay that failed validation had an attempt with no rate before it.
   */
  const recordPaidRate = () => {
    const rate = validateShippingSelection(product, shipping, shippingArrangedSeparately, shippingProviders.length);
    if (!rate.valid) return;
    recordShippingSelected();
  };

  /**
   * A signed-in buyer's NEW-card payment intent, for the one-step page: the
   * intent the old "Add Payment Method" made (addCreditCard), made at Pay with
   * the rate and coupon on screen. The card form is already mounted in
   * deferred mode (components/guest-payment.tsx SignedInPayment).
   *
   * Null when it refused; it has already told the buyer why.
   */
  const prepareSignedInPayment = async (): Promise<string | null> => {
    if (!loggedIn || !product) return null;
    if (sellerSetupErrorShownRef.current) return null;

    const sellerValidation = validateSellerTrustLevel(product);
    if (!sellerValidation.valid) {
      sellerSetupErrorShownRef.current = true;
      createNotification({ type: "error", msg: sellerValidation.error!, dismissed: false });
      trackError('checkout', 'validation_error', 'Seller has not completed payout setup', {
        step: 'payment_attempted', listing_id: product?.id, seller_id: product?.user?.id, user_id: profile?.id,
      });
      return null;
    }
    if (!shippingSelectionIsValid('payment_attempted')) return null;
    if (confirming) return null;

    // The rate was recorded at Pay, before the attempt (recordPaidRate).
    setPaymentMethodId(null);
    setConfirming(true);
    try {
      const response = await createPaymentIntent(product, undefined, defaultAddress || null, selectShippingRateAtIndex(shippingProviders, shipping));
      return response?.clientSecret ?? null;
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error creating payment intent'), ErrorSeverity.ERROR, { action: 'prepareSignedInPayment', component: 'CheckoutProvider' });
      createNotification({ type: "error", msg: errorMessageOr(error, 'Failed to create payment intent'), dismissed: false });
      return null;
    } finally {
      setConfirming(false);
    }
  };

  /**
   * Whether the buyer has a rate to pay for, telling them when not. Every
   * listing that checks out ships, so there are no exemptions.
   */
  const shippingSelectionIsValid = (funnelStep: 'payment_attempted' | 'shipping_selected'): boolean => {
    const result = validateShippingSelection(
      product, shipping, shippingArrangedSeparately, shippingProviders.length, shippingQuoteFailed,
    );
    if (result.valid) return true;
    createNotification({ type: 'error', msg: result.error!, dismissed: false });
    trackError('checkout', 'validation_error', result.error!, {
      step: funnelStep,
      listing_id: product?.id,
      user_id: profile?.id,
    });
    return false;
  };

  // Complete checkout - refactored to use validation services
  /**
   * The guest's payment intent, for the one-step page: everything the old
   * "Proceed to Payment" did up to the intent, and nothing after it. The card
   * form is already on the page (Stripe's deferred-intent mode,
   * components/guest-payment.tsx), so this is called at Pay, with the shipping
   * and coupon the buyer is paying for, and hands back the secret to confirm.
   *
   * Null when it refused; it has already told the buyer why.
   */
  const prepareGuestPayment = async (): Promise<string | null> => {
    if (sellerSetupErrorShownRef.current) return null;

    // Validate required product data using service
    const productValidation = validateProductForCheckout(product);
    if (!productValidation.valid) {
      createNotification({ type: "error", msg: productValidation.error!, dismissed: false });
      trackError('checkout', 'validation_error', productValidation.error!, { step: 'payment_attempted' });
      return null;
    }

    // Check if seller has completed Stripe setup using validation service
    const sellerValidation = validateSellerTrustLevel(product);
    if (!sellerValidation.valid) {
      sellerSetupErrorShownRef.current = true;
      createNotification({
        type: "error",
        msg: sellerValidation.error!,
        dismissed: false
      });
      trackError(
        'checkout',
        'validation_error',
        'Seller has not completed payout setup',
        {
          step: 'payment_attempted',
          listing_id: product?.id,
          seller_id: product?.user?.id
        }
      );
      return null;
    }

    // A rate to pay for. Every listing that checks out ships.
    if (!shippingSelectionIsValid('payment_attempted')) return null;

    if (confirming) return null;
    // The rate was recorded at Pay, before the attempt (recordPaidRate). Here,
    // after setConfirming, it fired after `payment_attempted`, and a throw from
    // it left `confirming` set so every later Pay did nothing.
    setConfirming(true);

    // CJ variant: same client-side override + variantVid threading as the
    // authenticated path. The server validates the variant price against
    // LISTINGS_METADATA.externalData.variants — this is purely for display.
    try {
      const response = await fetch('/api/checkout/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildGuestPaymentIntentBody({
          product,
          email: guestEmailState.guestEmail,
          shippingAddress: guestShippingAddress,
          shippingRate: shippingProviders[shipping],
          selectedVariant,
          couponsActive,
          couponApplicationMode,
          couponId,
          couponCode,
          // Partner attribution: read from URL `?p=` or localStorage so it
          // persists to checkout even when the partner_code cookie is gone.
          partnerCode: getPartner(),
        })),
      });

      const rawData = await response.json();

      // Validate response with Zod schema
      const parseResult = CreatePaymentIntentResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        logErrorEvent(new Error('completeCheckout guest validation failed'), ErrorSeverity.ERROR, { action: 'completeCheckout', component: 'CheckoutProvider', metadata: { zodErrors: parseResult.error.flatten() } } as ErrorContext);
        throw new Error('Invalid response format from payment intent API');
      }

      const data = parseResult.data;

      if (!data.success) {
        const errData = data as CreatePaymentIntentError;
        if (errData.couponFailed) {
          removeCoupon();
          createNotification({ type: 'error', msg: errData.error, dismissed: false });
          setConfirming(false);
          return null;
        }

        // Handle listing status errors using service
        if (errData.listingStatus && errData.listingStatus !== 1) {
          const statusMessage = getListingStatusMessage(errData.listingStatus);

          createNotification({
            type: "error",
            msg: statusMessage,
            dismissed: false,
          });
          trackError('checkout', 'checkout_redirect', statusMessage, {
            listing_id: product?.id, listing_status: errData.listingStatus,
            redirect_to: `/product/${product?.id}`, step: 'guest_payment_intent_creation',
          });
          logErrorEvent(statusMessage, ErrorSeverity.WARNING, {
            component: 'checkout', action: 'checkout_redirect', listing_id: Number(product?.id),
            funnel_type: 'checkout', funnel_step: 'guest_payment_intent_creation',
            tags: { listing_status: String(errData.listingStatus) },
          });
          push(`/product/${product?.id}`);
          setConfirming(false);
          return null;
        }

        throw new Error(errData.error || 'Failed to create payment intent');
      }

      if (!data.clientSecret) {
        throw new Error('Invalid response from payment intent API');
      }

      setConfirming(false);
      googleEvent("checkout_guest_payment_intent_created");
      return data.clientSecret;
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error creating guest payment intent'), ErrorSeverity.ERROR, { action: 'completeCheckout', component: 'CheckoutProvider' });
      // Use service to extract error message
      const errorMsg = extractErrorMessage(error, 'Failed to create payment intent');
      createNotification({
        type: "error",
        msg: errorMsg,
        dismissed: false,
      });

      // Track payment error
      trackError(
        'checkout',
        'stripe_error',
        errorMsg,
        {
          event_step: 'payment_attempted',
          listing_id: product?.id,
        }
      );

      setConfirming(false);
      return null;
    }
  };

  const completeCheckout = async () => {
    // Prevent repeated error spam if we've already shown the seller setup error
    if (sellerSetupErrorShownRef.current) {
      return;
    }

    // A guest pays on the one page: components/guest-payment.tsx calls
    // prepareGuestPayment at Pay. There is no guest step 2 to go to.
    if (!loggedIn) return;

    // Authenticated checkout
    if (confirming) return;

    // Validate payment method using service
    const paymentValidation = validatePaymentMethod(paymentMethodId);
    if (!paymentValidation.valid) {
      // Pay did nothing at all here: the card list can lose its selection (a
      // card removed, a refresh that failed), and the buyer got no word of it.
      createNotification({ type: 'error', msg: choosePaymentMethodMsg, dismissed: false });
      // Put the saved cards back on screen, or the add-card form when none are left.
      setShowAddCardButton(paymentMethods.length > 0);
      trackError('checkout', 'validation_error', paymentValidation.error!, {
        step: 'payment_attempted',
        listing_id: product?.id,
        user_id: profile?.id
      });
      return;
    }

    // Check if seller has completed Stripe setup using validation service
    const sellerValidation = validateSellerTrustLevel(product);
    if (!sellerValidation.valid) {
      sellerSetupErrorShownRef.current = true;
      createNotification({
        type: "error",
        msg: sellerValidation.error!,
        dismissed: false
      });
      trackError(
        'checkout',
        'validation_error',
        'Seller has not completed payout setup',
        {
          step: 'payment_attempted',
          listing_id: product?.id,
          seller_id: product?.user?.id,
          user_id: profile?.id
        }
      );
      return;
    }

    // The same rule the guest path runs. The signed-in path never checked, so
    // a quote that failed charged a shippable parcel $0 shipping.
    if (!shippingSelectionIsValid('payment_attempted')) return;

    // One page: no step change recorded the rate (see recordShippingSelected).
    recordShippingSelected();
    setConfirming(true);

    /**
     * The saved-card charge is a payment attempt, and it was not recorded as one.
     *
     * `payment_attempted` was emitted only from the card form a GUEST fills in
     * (now components/guest-payment.tsx) -- so a signed-in shopper paying with
     * a card already on file went straight from shipping to complete. The August 27 purchase on
     * production did exactly that: it completed with no payment attempt in the
     * table, which reads on the chart as a completion out of nowhere.
     *
     * immediate: the success popup navigates away from here.
     */
    trackFunnelEvent(
      'checkout',
      'payment_attempted',
      buildPaymentAttemptedEventData(product, getCheckoutSessionId(), 'saved_card'),
      { ...buildFunnelUserOptions(profile?.id), immediate: true },
    );
    updateAbandonmentStep('checkout', 'payment_attempted', { listing_id: product?.id });

    // createPaymentIntent answers its own failures with null, but throws when
    // the listing fails validateProductForCheckout (that check sits outside its
    // try). Uncaught, `confirming` stayed true and every later Pay on the page
    // did nothing, with no word to the buyer.
    let paymentIntentResponse: Awaited<ReturnType<typeof createPaymentIntent>>;
    try {
      paymentIntentResponse = await createPaymentIntent(
        product,
        paymentMethodId,
        defaultAddress,
        shippingProviders[shipping],
      );
    } catch (error: unknown) {
      logErrorEvent(toTrackedError(error, 'Error creating saved-card payment intent'), ErrorSeverity.ERROR, { action: 'completeCheckout', component: 'CheckoutProvider' });
      createNotification({ type: "error", msg: errorMessageOr(error, 'Failed to create payment intent'), dismissed: false });
      setConfirming(false);
      return;
    }

    // A saved card that did not go through is a failed attempt, as on the card
    // form; before, the funnel showed an attempt with nothing after it.
    const savedCardAttempt = buildPaymentAttemptedEventData(product, getCheckoutSessionId(), 'saved_card');
    const reportSavedCardFailure = (errorCode: string) => {
      trackFunnelEvent(
        'checkout',
        'payment_failed',
        buildPaymentFailedEventData(savedCardAttempt, { stage: errorCode === 'refused' ? 'intent' : 'stripe', errorCode }),
        { ...buildFunnelUserOptions(profile?.id), immediate: true },
      );
    };

    if (!paymentIntentResponse) {
      // createPaymentIntent has logged the error and told the buyer.
      reportSavedCardFailure('refused');
      setConfirming(false);
      return;
    }

    // Check payment status using service functions
    if (isPaymentRequiresConfirmation(paymentIntentResponse.status)) {
      setConfirming(false);
      createNotification({ type: "error", msg: requiresConfirmationMsg, dismissed: false });
      return;
    }

    if (isPaymentSuccessStatus(paymentIntentResponse.status)) {
      // Use service to build purchase event data
      const purchaseEventData = buildPurchaseEventData(
        product,
        paymentIntentResponse.paymentIntentId!,
        Number(shipAmount)
      );
      googleEvent("purchase", purchaseEventData);

      setConfirming(false);
      void popSuccess(paymentIntentResponse.paymentIntentId, { method: { source: 'saved_card', type: 'card' } });
      return;
    }

    // Anything else, most often `requires_action` (the bank wants 3D Secure,
    // which an off-page saved-card charge cannot show), used to end here with
    // Pay back and no word to the buyer and no row anywhere.
    const status = paymentIntentResponse.status ?? 'unknown';
    reportSavedCardFailure(`status_${status}`);
    logErrorEvent(new Error(`Saved-card payment not completed: ${status}`), ErrorSeverity.WARNING, {
      action: 'completeCheckout',
      component: 'CheckoutProvider',
      extra: { paymentStatus: status, listingId: product?.id },
    });
    createNotification({ type: 'error', msg: savedCardNotCompletedMsg, dismissed: false });
    setConfirming(false);
  };

  // Confirm shipping
  const confirmShipping = async () => {
    if (confirming) return;

    // Prevent repeated error spam if we've already shown the seller setup error
    if (sellerSetupErrorShownRef.current) {
      return;
    }

    // Check if seller has completed Stripe setup using validation service
    const sellerValidation = validateSellerTrustLevel(product);
    if (!sellerValidation.valid) {
      sellerSetupErrorShownRef.current = true;
      createNotification({
        type: "error",
        msg: sellerValidation.error!,
        dismissed: false
      });

      // Track validation error
      trackError(
        'checkout',
        'validation_error',
        'Seller has not completed payout setup',
        {
          step: 'shipping_selected',
          listing_id: product?.id,
          seller_id: product?.user?.id,
        }
      );

      return;
    }

    // No moving to payment without a rate to pay for, unless the quote said the
    // two of them arrange shipping themselves.
    if (!shippingSelectionIsValid('shipping_selected')) return;

    setConfirming(true);

    // Use service to build begin_checkout event data
    const beginCheckoutData = buildBeginCheckoutEventData(product);
    googleEvent("begin_checkout", beginCheckoutData);

    await getPaymentMethods();
    setConfirming(false);
    setStep(2);
    setShowAddCardButton(true);

    const selectedRate = shippingProviders[shipping];

    // Update abandonment tracking to payment step
    updateAbandonmentStep(
      'checkout',
      'shipping_selected',
      buildShippingSelectedAbandonmentContext(product, profile?.id, selectedRate),
    );

    trackFunnelEvent(
      'checkout',
      'shipping_selected',
      buildShippingSelectedEventData(product, selectedRate, getCheckoutSessionId()),
      buildFunnelUserOptions(profile?.id),
    );

    googleEvent("checkout_confirm_shipping");
  };

  // Navigation
  const gotoStep1 = () => {
    setStep(1);
    setShowAddCardButton(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const gotoStep2 = async () => {
    await confirmShipping();
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1000);
  };

  // Success popup
  const popSuccess = async (paymentIntentId?: string, paid: PaidOrderDetails = {}) => {
    // Disable abandonment tracking - checkout completed!
    disableAbandonmentTracking('checkout');
    // Bought: the draft has done its job, and a guest is signed into the
    // account this purchase made, so checkout fills from that next time.
    if (!loggedIn) clearGuestCheckoutDraft();
    rememberPurchase(product?.id);

    // A wallet purchase never passed through the page's form, so what it paid
    // for arrives here rather than from state this render closed over.
    const paidProviders = paid.shippingProviders ?? shippingProviders;
    const paidShipping = paid.shipping ?? shipping;
    const paidEmail = paid.email ?? guestEmailState.guestEmail;
    const paidArranged = paid.arrangedSeparately ?? shippingArrangedSeparately;

    const selectedRate = paidProviders[paidShipping];
    // immediate: the success popup navigates away, so don't wait for the batch flush
    // `complete`, not `completed`. Every reader of this event -- the Completed
    // milestone, the flow-status derivation in the timeline API, and every other
    // funnel in the app -- looks for `complete`, so the name it was emitted under
    // could never match any of them. The checkout funnel has recorded zero
    // completions for its entire history.
    trackFunnelEvent(
      'checkout',
      'complete',
      buildCheckoutCompleteEventData(product, selectedRate, getCheckoutSessionId(), paymentIntentId, {
        source: paid.method?.source ?? (loggedIn ? 'card_form' : 'guest_card_form'),
        type: paid.method?.type,
        isGuest: !loggedIn,
      }),
      { ...buildFunnelUserOptions(profile?.id), immediate: true },
    );
    // The card form's GA purchase. A saved card and a wallet send their own; the
    // old signed-in card form sent this one, and the one page had lost it.
    const source = paid.method?.source;
    if (product && paymentIntentId && (source === 'card_form' || source === 'guest_card_form')) {
      googleEvent('purchase', buildPurchaseEventData(product, paymentIntentId, Number(selectedRate?.amount ?? 0)));
    }

    clearCheckoutSessionId();

    // Import CheckoutSuccess here to avoid circular deps
    const CheckoutSuccess = (await import('../components/checkout-success')).default;

    // Custom close handler that redirects to purchase page
    const handleClose = () => {
      setPopupOpen(false);
      push(`/account/purchases/${product?.id}`);
    };

    /**
     * Sign a guest into the account their purchase just made.
     *
     * The webhook creates a USERS row from the email they typed into the order,
     * so by now they have an account and no way into it — "View Order" would
     * push them at /account/purchases/<id>, the auth guard would see
     * `unauthenticated`, and they would land on a bare login screen.
     *
     * So: wait for the webhook, mail a code to that same address, and open the
     * modal already holding it at the code step. `returnTo` carries them to the
     * order once they are in.
     *
     * The card is charged by the time any of this runs. Every step fails soft
     * and returns false, which puts the buyer back on the ordinary path with
     * their receipt intact.
     */
    const claimGuestOrder = async (): Promise<boolean> => {
      // Only ever handed to the popup for a guest (see onClaimOrder below), so
      // `loggedIn` is false here by construction.
      const email = paidEmail;
      if (!email || !paymentIntentId) return false;

      const order = await waitForGuestOrder({ paymentIntentId, email });
      if (!order) return false;

      const sent = await sendEmailLoginCode({ email, funnelSessionId: getFunnelSessionId('login') });
      if (!sent.success) return false;

      setPopupOpen(false);
      setLoginPrefill(email);
      setLoginView('login-otp');
      setLoginOpen(true);
      void push(order.redirectUrl);
      return true;
    };

    setPopupContents(
      <CheckoutSuccess
        onClaimOrder={loggedIn ? null : claimGuestOrder}
        estimatePossible={estimatePossible}
        arrangedSeparately={paidArranged}
        shipping={paidShipping}
        shippingProviders={paidProviders}
        item={product}
        router={router}
        setPopupOpen={setPopupOpen}
        couponDiscount={paid.coupon !== undefined ? (paid.coupon?.discount ?? 0) : couponDiscount}
        couponCode={paid.coupon !== undefined ? (paid.coupon?.code ?? null) : couponCode}
        couponApplicationMode={paid.coupon !== undefined ? (paid.coupon?.mode ?? '') : couponApplicationMode}
      />,
      handleClose
    );
    setPopupOpen(true);
  };

  /**
   * A wallet purchase went through. The page is brought up to date with what
   * was bought -- the wallet's email and address, and the rate charged -- so
   * the success popup, the receipt and the "view your order" sign-in all read
   * the same order the card form would have produced.
   */
  const finishExpressOrder = async (paymentIntentId: string, paid: ExpressPaidOrder) => {
    const index = paid.rate ? Math.max(0, paid.rates.findIndex((r) => r.id === paid.rate?.id)) : 0;
    // A signed-in buyer's address is in their address book now; the guest
    // fields stay as they were.
    if (!loggedIn) {
      setGuestEmailState({ guestEmail: paid.email });
      setGuestShippingAddress(paid.shippingAddress);
    }
    setShippingProviders(paid.rates);
    setShipping(index);
    setShippingArrangedSeparately(!paid.rate);
    googleEvent('purchase', buildPurchaseEventData(product, paymentIntentId, paid.rate?.amount ?? 0));
    await popSuccess(paymentIntentId, {
      email: paid.email,
      shippingProviders: paid.rates,
      shipping: index,
      arrangedSeparately: !paid.rate,
      method: { source: 'express', type: paid.wallet },
    });
  };

  // Reset seller setup error flag when product changes
  /**
   * Checkout entry.
   *
   * `started` used to be emitted from confirmShipping -- the click that LEAVES
   * the shipping step -- so it landed after address_entered and
   * shipping_calculated, in the same second as shipping_selected, and only 21 of
   * 165 checkout sessions in 90 days ever reached it. Every rate on the checkout
   * chart divides by that number, so the denominator was a population that had
   * already converted most of the way and rates could exceed 100%.
   *
   * Abandonment tracking started at the same late point, which is why 90 days
   * produced 19 abandoned sessions out of 165: leaving before the shipping
   * confirmation -- the most common way to abandon a checkout -- was not
   * instrumented at all.
   *
   * Both begin on entry now. The ref keys on the listing so a second checkout in
   * the same mount records its own start, and re-renders record nothing.
   *
   * Entry is the page showing, not the listing arriving: waiting for the status
   * check and the listing fetch put `started` a median 2.4 s (p90 4.3 s) after
   * the checkout page view on prod (14 days to 2026-10-26), and a buyer who
   * left inside that window left no row at all. The id comes from the URL; the
   * listing's title, price and seller ride along when they are already known.
   */
  const startedForListingRef = React.useRef<string | null>(null);
  const routeListingId = router.isReady && router.query?.id ? String(router.query.id) : null;
  React.useEffect(() => {
    const listingId = product?.id ?? routeListingId;
    if (!listingId) return;
    if (startedForListingRef.current === String(listingId)) return;
    startedForListingRef.current = String(listingId);

    trackFunnelEvent('checkout', 'started', buildCheckoutStartedEventData(listingId, {
      title: product?.title,
      price: product?.price,
      sellerId: product?.user?.id,
    }));
    // eslint-disable-next-line react-hooks/set-state-in-effect -- tells the page its step events may follow; set once per listing, after `started` is queued
    setStartedListingId(listingId);

    setupAbandonmentTracking(
      'checkout',
      getCheckoutSessionId(),
      'started',
      buildCheckoutStartedAbandonmentContext(listingId, profile?.id, loggedIn),
    );
  }, [product?.id, routeListingId, product?.title, product?.price, product?.user?.id, profile?.id, loggedIn]);

  // Started before the session may have loaded: keep who the shopper is current
  // on the abandonment it would send.
  React.useEffect(() => {
    updateAbandonmentMetadata('checkout', { user_id: profile?.id, is_guest: loggedIn ? 0 : 1 });
  }, [profile?.id, loggedIn]);

  /**
   * Leaving checkout ends the attempt.
   *
   * This is its own mount-scoped effect rather than a cleanup on the one above,
   * and that matters: the effect above re-runs whenever the shopper's identity
   * changes, and a mid-flow login would fire its cleanup -- recording an
   * abandonment for a checkout the person is still standing in.
   *
   * A completed checkout has already called disableAbandonmentTracking, so the
   * unmount that follows the success popup records nothing.
   */
  React.useEffect(() => () => endAbandonmentTracking('checkout'), []);

  // Keep what a guest types, for the next reload. Empty values are not saved,
  // so a blank form mounting can never wipe a draft. Signed-in buyers fill from
  // their account instead.
  React.useEffect(() => {
    if (loggedIn) return;
    const email = guestEmailState.guestEmail.trim();
    const hasAddress = Object.entries(guestShippingAddress).some(
      ([key, value]) => key !== 'country' && String(value ?? '').trim() !== '',
    );
    if (!email && !hasAddress) return;
    saveGuestCheckoutDraft({
      ...(email ? { email } : {}),
      ...(hasAddress ? { address: guestShippingAddress } : {}),
    });
  }, [loggedIn, guestEmailState.guestEmail, guestShippingAddress]);

  const guestPickedRate = shippingProviders[shipping];
  React.useEffect(() => {
    if (loggedIn || !guestPickedRate || !product?.id) return;
    saveGuestCheckoutDraft({
      shipping: {
        listingId: String(product.id),
        provider: guestPickedRate.provider.name,
        service: guestPickedRate.service,
      },
    });
  }, [loggedIn, guestPickedRate, product?.id]);

  // `?widget=classic|styled|off` previews a guest checkout widget in this
  // browser; see utils/checkout-widget-preview.ts. Set before the guest reaches
  // payment, which is when the intent route reads it.
  const widgetQuery = router.query?.widget;
  React.useEffect(() => {
    const cookie = widgetPreviewCookie(widgetQuery);
    if (cookie) document.cookie = cookie;
  }, [widgetQuery]);

  React.useEffect(() => {
    sellerSetupErrorShownRef.current = false;
  }, [product?.id]);

  const value = {
    // State
    product,
    productResolved,
    startedListingId,
    guestDraftPrefill,
    shippingProviders,
    shipping,
    paymentMethods,
    paymentMethodId,
    countries,
    showAddCardButton,
    clientSecret,
    widgetVariant,
    transactionId,
    loadingPaymentMethods,
    loadedPaymentMethods,
    estimatePossible,
    shippingArrangedSeparately,
    shippingQuoteFailed,
    retryShippingQuote,
    step,
    stripePromise,
    contactingStripe,
    confirming,
    loadingShipping,
    loadingAddresses,
    guestEmailState,
    guestEmailErrors,
    guestEmailFocus,
    guestEmailFilled,
    guestShippingAddress,

    // Setters
    setProduct,
    setShipping,
    setPaymentMethodId,
    setShowAddCardButton,
    setClientSecret,
    setStep,
    setCountries,
    setGuestEmailState,
    setGuestEmailErrors,
    setGuestEmailFocus,
    setGuestEmailFilled,
    setGuestShippingAddress,
    setEstimatePossible,

    // Actions
    getShippingProviders,
    getPaymentMethods,
    setDefaultAddress,
    completeCheckout,
    prepareGuestPayment,
    prepareSignedInPayment,
    recordPaidRate,
    confirmShipping,
    addCreditCard,
    gotoStep1,
    gotoStep2,
    popSuccess,
    getProduct,
    checkListingStatus,
    verifyStripeReturn,
    geocodeTypedAddress,
    quoteExpressRates,
    createExpressPaymentIntent,
    finishExpressOrder,

    // Coupon
    couponId,
    couponCode,
    couponDiscount,
    couponName,
    couponType,
    couponValidating,
    couponError,
    couponApplicationMode,
    applyCouponCode,
    removeCoupon,

    // Computed
    defaultAddress,
    selectedShipping,
    shipAmount,
    subTotal,
    stripeFee,
    salesTax,
    totalPrice,
  };

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
};
